import { gql } from "@apollo/client";
import { EMessageType, IMessage, IMessageComment } from "types/Message.type";

export interface ICreateMessageInput {
  type: EMessageType;
  text?: string;
  questionName?: string;
  questionOptions?: string[];
  skill?: string;
  thumbnail?: string;
  tags?: string[];
}

const CREATE_MESSAGE = gql`
  mutation createMessage($createMessageInput: CreateMessageInput!) {
    createMessage(createMessageInput: $createMessageInput) {
      id
    }
  }
`;

export interface ICreateMessageCommentInput {
  message: string;
  text: string;
}

const CREATE_MESSAGE_COMMENT = gql`
  mutation createMessageComment(
    $createMessageCommentInput: CreateMessageCommentInput!
  ) {
    createMessageComment(
      createMessageCommentInput: $createMessageCommentInput
    ) {
      id
      message
      text
      createdAt
      createdBy {
        id
        name
        lastname
        profileImage
      }
      reactions {
        emoji
        user
      }
    }
  }
`;

export interface IMessageReactionInput {
  message: string;
  emoji: string;
}

const MESSAGE_REACTION = gql`
  mutation messageReaction($messageReactionInput: MessageReactionInput!) {
    messageReaction(messageReactionInput: $messageReactionInput) {
      emoji
      user
      reactionUser {
        id
        name
        lastname
        profileImage
      }
    }
  }
`;

export interface ICommentMessageReactionInput {
  commentMessage: string;
  emoji: string;
}

const COMMENT_MESSAGE_REACTION = gql`
  mutation commentMessageReaction(
    $commentMessageReactionInput: CommentMessageReactionInput!
  ) {
    commentMessageReaction(
      commentMessageReactionInput: $commentMessageReactionInput
    ) {
      emoji
      user
      reactionUser {
        id
        name
        lastname
        profileImage
      }
    }
  }
`;

export interface IMessagePollAnswerInput {
  message: string;
  index: number;
}

const MESSAGE_POLL_ANSWER = gql`
  mutation messagePollAnswer($messagePollAnswerInput: MessagePollAnswerInput!) {
    messagePollAnswer(messagePollAnswerInput: $messagePollAnswerInput) {
      user
      index
    }
  }
`;

const DELETE_MESSASGE = gql`
  mutation DeleteMessage($message: String!) {
    deleteMessage(message: $message) {
      id
    }
  }
`;

const DELETE_MESSAGE_COMMENT = gql`
  mutation DeleteMessageComment($messageComment: String!) {
    deleteMessageComment(messageComment: $messageComment) {
      id
    }
  }
`;

export interface IUpdateMessageInput {
  id: string;
  text: string;
  thumbnail?: string;
  tags?: string[];
}

export interface IUpdateMessageRes {
  updateMessage: IMessage;
}

const UPDATE_MESSAGE = gql`
  mutation updateMessage($updateMessageInput: UpdateMessageInput!) {
    updateMessage(updateMessageInput: $updateMessageInput) {
      id
      edited
      isVoted
      createdAt
      updatedAt
      type
      text
      question
      thumbnail
      questionOptions
      createdBy {
        id
        name
        lastname
        profileImage
      }
      answer {
        id
        text
        votes
      }
      comments {
        id
        edited
        text
        createdAt
        updatedAt
        message
        createdBy {
          id
          name
          lastname
          profileImage
        }
        reactions {
          emoji
          user
        }
      }
      tags
      reactions {
        emoji
        user
      }
    }
  }
`;

export interface IUpdateMessageCommentInput {
  id: string;
  text: string;
}

export interface IUpdateMessageCommentRes {
  updateMessageComment: IMessageComment;
}

const UPDATE_MESSAGE_COMMENT = gql`
  mutation UpdateMessageComment(
    $updateMessageCommentInput: UpdateMessageCommentInput!
  ) {
    updateMessageComment(
      updateMessageCommentInput: $updateMessageCommentInput
    ) {
      id
      text
      createdAt
      updatedAt
      message
      createdBy {
        id
        name
        lastname
        profileImage
      }
      reactions {
        emoji
        user
      }
    }
  }
`;

const MARK_CAPSULE_AS_SEEN = gql`
  mutation AddViewToCapsule($message: String!) {
    addViewToCapsule(message: $message) {
      id
    }
  }
`;

const ADD_VIEW_TO_STORY = gql`
  mutation addViewToStory($storyId: String!) {
    addViewToStory(storyId: $storyId)
  }
`;

export interface ICreateStoryInput {
  thumbnail: string;
  text?: string;
  ownerUserId?: string;
  sourceMessageId?: string;
  sourceMessageType?: string;
  isAutoGenerated?: boolean;
}

const CREATE_STORY = gql`
  mutation createStory($createStoryInput: CreateStoryInput!) {
    createStory(createStoryInput: $createStoryInput) {
      id
      createdAt
      text
      thumbnail
      ownerUserId
      sourceMessageId
      sourceMessageType
      linkUrl
      previewTitle
      previewDescription
      isAutoGenerated
      createdBy {
        id
        name
        lastname
        profileImage
      }
      ownerUser {
        id
        name
        lastname
        profileImage
      }
      reactions {
        emoji
        user
        reactionUser {
          id
          name
          lastname
          profileImage
        }
      }
    }
  }
`;

const CREATE_STORY_FROM_MESSAGE = gql`
  mutation CreateStoryFromMessage($messageId: String!) {
    createStoryFromMessage(messageId: $messageId) {
      id
      createdAt
      text
      thumbnail
      ownerUserId
      sourceMessageId
      sourceMessageType
      linkUrl
      previewTitle
      previewDescription
      isAutoGenerated
      createdBy {
        id
        name
        lastname
        profileImage
      }
      ownerUser {
        id
        name
        lastname
        profileImage
      }
      reactions {
        emoji
        user
        reactionUser {
          id
          name
          lastname
          profileImage
        }
      }
    }
  }
`;

const PUBLISH_MESSAGE_TO_MY_STORY = gql`
  mutation PublishMessageToMyStory($messageId: String!) {
    publishMessageToMyStory(messageId: $messageId) {
      id
      createdAt
      text
      thumbnail
      ownerUserId
      sourceMessageId
      sourceMessageType
      linkUrl
      previewTitle
      previewDescription
      isAutoGenerated
      createdBy {
        id
        name
        lastname
        profileImage
      }
      ownerUser {
        id
        name
        lastname
        profileImage
      }
      reactions {
        emoji
        user
        reactionUser {
          id
          name
          lastname
          profileImage
        }
      }
    }
  }
`;

const SEND_STORY_INVITATION = gql`
  mutation SendStoryInvitation($email: String!) {
    sendStoryInvitation(email: $email)
  }
`;

const DELETE_STORY = gql`
  mutation DeleteStory($id: String!) {
    deleteStory(id: $id) {
      id
    }
  }
`;

export interface IStoryReactionInput {
  story: string;
  emoji: string;
}

const STORY_REACTION = gql`
  mutation storyReaction($storyReactionInput: StoryReactionInput!) {
    storyReaction(storyReactionInput: $storyReactionInput) {
      emoji
      user
      reactionUser {
        id
        name
        lastname
        profileImage
      }
    }
  }
`;

export default {
  CREATE_MESSAGE,
  CREATE_MESSAGE_COMMENT,
  MESSAGE_REACTION,
  MESSAGE_POLL_ANSWER,
  DELETE_MESSASGE,
  DELETE_MESSAGE_COMMENT,
  COMMENT_MESSAGE_REACTION,
  UPDATE_MESSAGE,
  UPDATE_MESSAGE_COMMENT,
  MARK_CAPSULE_AS_SEEN,
  ADD_VIEW_TO_STORY,
  CREATE_STORY,
  CREATE_STORY_FROM_MESSAGE,
  PUBLISH_MESSAGE_TO_MY_STORY,
  SEND_STORY_INVITATION,
  DELETE_STORY,
  STORY_REACTION
};
